import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Grid from "@mui/material/Grid2";
import SwapVertIcon from "@mui/icons-material/SwapVert";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import { useSnackbar } from "notistack";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { AuthContext } from "../../context/AuthContext";
import { getDataWithToken } from "../../services/GetDataService";

const customeTextFeild = {
  "& label": {
    fontSize: "11px",
  },
  "& label.Mui-focused": {
    color: "#0F1624",
  },
  "& .MuiInput-underline:after": {
    borderBottomColor: "#B2BAC2",
  },
  "& .MuiOutlinedInput-input": {
    padding: "8px 12px",
  },
  "& .MuiOutlinedInput-root": {
    fontSize: "13px",
    "& fieldset": {
      borderRadius: "8px",
    },
    "&:hover fieldset": {
      borderColor: "#969696",
    },
    "&.Mui-focused fieldset": {
      borderColor: "#969696",
    },
  },
};

const buildModelRows = (branches, models = []) => {
  const modelMap = new Map();

  models.forEach((model) => {
    if (!model?._id) {
      return;
    }
    modelMap.set(model._id, {
      model_id: model._id,
      model_name: model?.name,
      counts: {},
    });
  });

  branches.forEach((branch) => {
    (branch?.models || []).forEach((model) => {
      if (!modelMap.has(model?.model_id)) {
        modelMap.set(model?.model_id, {
          model_id: model?.model_id,
          model_name: model?.model_name,
          counts: {},
        });
      }
      modelMap.get(model.model_id).counts[branch.branch_id] = Number(
        model?.repair_count || 0,
      );
    });
  });

  const withTotal = (model) => ({
    ...model,
    total: Object.values(model.counts).reduce(
      (sum, count) => sum + Number(count || 0),
      0,
    ),
  });

  if (models.length > 0) {
    return models
      .filter((model) => model?._id && modelMap.has(model._id))
      .map((model) => withTotal(modelMap.get(model._id)));
  }

  return Array.from(modelMap.values()).map(withTotal);
};

const RepairReport = () => {
  const { ifixit_admin_panel, logout } = useContext(AuthContext);
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [startDate, setStartDate] = useState(dayjs());
  const [endDate, setEndDate] = useState(dayjs());
  const [deviceId, setDeviceId] = useState("None");
  const [deviceList, setDeviceList] = useState([]);
  const [modelList, setModelList] = useState([]);
  const [message, setMessage] = useState("");
  const [sortBy, setSortBy] = useState(null);
  const [sortDirection, setSortDirection] = useState("desc");

  const handleSnakbarOpen = (msg, vrnt) => {
    enqueueSnackbar(msg, {
      variant: vrnt,
      autoHideDuration: vrnt === "error" ? 3000 : 1000,
    });
  };

  const getRepairReport = async (fromDate, toDate) => {
    const url = `/api/v1/report/branch-wise-repair-count?startDate=${dayjs(
      fromDate,
    ).format("YYYY-MM-DD")}&endDate=${dayjs(toDate).format("YYYY-MM-DD")}`;
    const response = await getDataWithToken(url, ifixit_admin_panel?.token);

    if (response?.status === 401) {
      logout();
      return;
    }

    if (response?.status >= 200 && response?.status < 300) {
      const nextRows = Array.isArray(response?.data?.data)
        ? response.data.data
        : [];
      setReportData(response?.data ?? null);
      setLoaded(true);
      setMessage(nextRows.length < 1 ? "No data found" : "");
    } else {
      setLoaded(false);
      setMessage("");
      handleSnakbarOpen(response?.data?.message, "error");
    }
  };

  const getModelList = async (id) => {
    if (!id || id === "None") {
      setModelList([]);
      return;
    }

    const url = `/api/v1/model/device-model?deviceId=${id}`;
    const response = await getDataWithToken(url, ifixit_admin_panel?.token);

    if (response?.status === 401) {
      logout();
      return;
    }

    if (response?.status >= 200 && response?.status < 300) {
      setModelList([...(response?.data?.data || [])].reverse());
    } else {
      setModelList([]);
      handleSnakbarOpen(response?.data?.message, "error");
    }
  };

  const handleSubmit = async () => {
    if (!startDate || !endDate || !deviceId || deviceId === "None") {
      handleSnakbarOpen("Please select device, from date and to date", "error");
      return;
    }

    setSortBy(null);
    setSortDirection("desc");
    setLoading(true);
    try {
      await Promise.all([
        getRepairReport(startDate, endDate),
        getModelList(deviceId),
      ]);
    } finally {
      setLoading(false);
    }
  };

  const getDeviceList = async () => {
    const url = "/api/v1/device/dropdownlist";
    const response = await getDataWithToken(url, ifixit_admin_panel?.token);

    if (response?.status === 401) {
      logout();
      return;
    }

    if (response?.status >= 200 && response?.status < 300) {
      setDeviceList(response?.data?.data || []);
    } else {
      handleSnakbarOpen(response?.data?.message, "error");
    }
  };

  const handleSort = (key) => {
    if (sortBy === key) {
      setSortDirection((current) => (current === "desc" ? "asc" : "desc"));
      return;
    }
    setSortBy(key);
    setSortDirection("desc");
  };

  const sortIcon = (key) => {
    if (sortBy !== key) {
      return <SwapVertIcon fontSize="small" />;
    }
    return sortDirection === "desc" ? (
      <ArrowDownwardIcon fontSize="small" />
    ) : (
      <ArrowUpwardIcon fontSize="small" />
    );
  };

  useEffect(() => {
    // getRepairReport(startDate, endDate);
    getDeviceList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const rows = Array.isArray(reportData?.data) ? reportData.data : [];
  const modelRows = buildModelRows(rows, modelList);
  const sortedModelRows = !sortBy
    ? modelRows
    : [...modelRows].sort((left, right) => {
        const leftValue =
          sortBy === "total" ? left.total : Number(left.counts[sortBy] || 0);
        const rightValue =
          sortBy === "total" ? right.total : Number(right.counts[sortBy] || 0);
        return sortDirection === "desc"
          ? rightValue - leftValue
          : leftValue - rightValue;
      });
  const columnCount = Math.max(rows.length + 2, 2);

  const pageLoading = () => {
    const content = [];
    for (let i = 0; i < 6; i++) {
      content.push(
        <TableRow key={i}>
          {[...Array(columnCount).keys()].map((cell) => (
            <TableCell key={cell}>
              <Skeleton />
            </TableCell>
          ))}
        </TableRow>,
      );
    }
    return content;
  };

  return (
    <>
      <Grid container columnSpacing={3} style={{ padding: "24px 0" }}>
        <Grid size={9}>
          <Typography
            variant="h6"
            gutterBottom
            component="div"
            sx={{ color: "#0F1624", fontWeight: 600 }}
          >
            Repair Models Report
          </Typography>
        </Grid>
      </Grid>
      <div
        style={{
          background: "#fff",
          border: "1px solid #EAECF1",
          borderRadius: "12px",
          overflow: "hidden",
          padding: "16px 0",
          boxShadow: "0px 1px 2px 0px rgba(15, 22, 36, 0.05)",
        }}
      >
        <Grid
          container
          justifyContent="space-between"
          alignItems="center"
          sx={{ px: 1.5, mb: 1.75 }}
          spacing={2}
        >
          <Grid size={{ xs: 4, sm: 4, md: 4, lg: 4, xl: 4 }}>
            <Typography
              variant="h6"
              gutterBottom
              component="div"
              sx={{ color: "#0F1624", fontWeight: 600, margin: 0 }}
            >
              Details
            </Typography>
          </Grid>
          <Grid size={{ xs: 8, sm: 8, md: 8, lg: 8, xl: 8 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Grid
                container
                justifyContent="right"
                alignItems="center"
                spacing={1}
              >
                <Grid size={{ xs: 12 }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      flexWrap: "wrap",
                    }}
                  >
                    <FormControl
                      variant="outlined"
                      size="small"
                      sx={{ ...customeTextFeild, width: "250px" }}
                    >
                      <InputLabel id="repair-report-device-label">
                        Device
                      </InputLabel>
                      <Select
                        fullWidth
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              maxHeight: 250,
                            },
                          },
                        }}
                        labelId="repair-report-device-label"
                        id="repair-report-device"
                        label="Device"
                        value={deviceId}
                        onChange={(event) => setDeviceId(event.target.value)}
                      >
                        <MenuItem value="None">None</MenuItem>
                        {deviceList
                          ?.filter(
                            (item) =>
                              !item?.name?.toLowerCase().includes("series"),
                          )
                          ?.map((item) => (
                            <MenuItem key={item?._id} value={item?._id}>
                              {item?.name}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                    &nbsp;&nbsp;&nbsp;
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="From Date"
                        value={startDate}
                        onChange={(newValue) => {
                          setStartDate(newValue);
                        }}
                        format="DD/MM/YYYY"
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { ...customeTextFeild, width: "180px" },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    &nbsp;&nbsp;&nbsp;
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        label="To Date"
                        value={endDate}
                        onChange={(newValue) => {
                          setEndDate(newValue);
                        }}
                        format="DD/MM/YYYY"
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            size: "small",
                            sx: { ...customeTextFeild, width: "180px" },
                          },
                        }}
                      />
                    </LocalizationProvider>
                    &nbsp;&nbsp;&nbsp;
                    <Button
                      variant="contained"
                      size="small"
                      disabled={loading}
                      onClick={handleSubmit}
                      sx={{ py: "7px" }}
                    >
                      Submit
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Grid>
        </Grid>
        <div
          style={{
            overflowX: "auto",
            minWidth: "100%",
            width: "Calc(100vw - 385px)",
            boxSizing: "border-box",
          }}
        >
          <TableContainer sx={{ maxHeight: "Calc(100vh - 250px)" }}>
            <Table stickyHeader aria-label="model wise repair count">
              <TableHead>
                <TableRow>
                  <TableCell style={{ whiteSpace: "nowrap" }}>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 0.5 }}
                    >
                      Model Name
                      <IconButton
                        size="small"
                        aria-label="Reverse model order"
                        disabled={modelList.length < 2}
                        onClick={() => {
                          setSortBy(null);
                          setModelList((current) => [...current].reverse());
                        }}
                      >
                        <SwapVertIcon fontSize="small" />
                      </IconButton>
                    </Box>
                  </TableCell>
                  {rows.map((branch) => (
                    <TableCell
                      key={branch?.branch_id}
                      align="right"
                      style={{ whiteSpace: "nowrap" }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                          gap: 0.5,
                        }}
                      >
                        <div>{branch?.branch_name}</div>
                      </Box>
                      {!loading && modelRows.length > 0 && (
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "flex-end",
                          }}
                        >
                          <div>
                            ({" "}
                            {Number(branch?.repair_count || 0).toLocaleString()}
                            )
                          </div>
                          <IconButton
                            size="small"
                            aria-label={`Sort by ${branch?.branch_name}`}
                            disabled={modelRows.length < 2}
                            onClick={() => handleSort(branch?.branch_id)}
                          >
                            {sortIcon(branch?.branch_id)}
                          </IconButton>
                        </Box>
                      )}
                    </TableCell>
                  ))}
                  <TableCell align="right" style={{ whiteSpace: "nowrap" }}>
                    <div>Total</div>
                    {!loading && modelRows.length > 0 && (
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "flex-end",
                        }}
                      >
                        <div>
                          {Number(reportData?.totalData || 0).toLocaleString()}
                        </div>
                        <IconButton
                          size="small"
                          aria-label="Sort by total"
                          disabled={modelRows.length < 2}
                          onClick={() => handleSort("total")}
                        >
                          {sortIcon("total")}
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {!loading &&
                  sortedModelRows.map((model) => (
                    <TableRow key={model.model_id}>
                      <TableCell
                        sx={{ minWidth: "160px", whiteSpace: "nowrap" }}
                      >
                        {model.model_name}
                      </TableCell>
                      {rows.map((branch) => (
                        <TableCell key={branch?.branch_id} align="right">
                          {Number(
                            model.counts[branch?.branch_id] || 0,
                          ).toLocaleString()}
                        </TableCell>
                      ))}
                      <TableCell align="right">
                        {Number(model.total || 0).toLocaleString()}
                      </TableCell>
                    </TableRow>
                  ))}
                {!loading && loaded && modelRows.length < 1 ? (
                  <TableRow>
                    <TableCell
                      colSpan={columnCount}
                      style={{ textAlign: "center" }}
                    >
                      <strong> {message || "No data found"}</strong>
                    </TableCell>
                  </TableRow>
                ) : null}
                {loading && pageLoading()}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      </div>
    </>
  );
};

export default RepairReport;
